import type { QBtnProps } from 'quasar'
import type { BrandModel, Permit, Presence, User, WithClass } from 'src/types'
import type { RouteNamedMap } from 'vue-router/auto-routes'
import dayjs from 'dayjs'
import { Dialog, Screen } from 'quasar'

export function getBrandLabel(brand_model_id?: number) {
  const brandModel = useApiStore().getObj(brand_model_id, 'brand_models') as BrandModel | undefined
  return useApiStore().getObjLabel(brandModel?.brand_id, 'brands')
}

export const isLevabolliFn = (id: number | string) => (useApiStore().getObj(Number(id), 'users') as User)?.type === userTypesEnum.Levabolli

// JS ordina sempre le chiavi numeriche di un oggetto in modo ascendente, a prescindere dall'ordine di inserimento:
// l'ordinamento per cognome/label va quindi applicato qui, non sul backend.
export function userSortKey(id: number | string) {
  const u = useApiStore().getObj(Number(id), 'users') as User | undefined
  return `${u?.surname ?? ''} ${u?.name ?? ''}`.toLowerCase()
}

export function sortIdsBy(ids: string[], labelFn: (id: string) => string) {
  return [...ids].sort((a, b) => labelFn(a).localeCompare(labelFn(b)))
}

export function clientLabelOf(presencesByDay: Record<string, Presence[]>) {
  return (Object.values(presencesByDay).flat()[0]?.client.label ?? '').toLowerCase()
}

export function isExpired(d: string) {
  return dayjs(d).isBefore(dayjs(), 'day')
}

export function getActionLabel(p: Presence, isLevabolli: boolean) {
  let label = ''
  if (hasTime(p)) {
    label += printTime(p)
    if (isLevabolli) label += ` | `
  }
  if (isLevabolli) {
    if (me.value.admin_can_see_amounts) {
      label += `€ ${currency(p.price)}`
      if (p.value) label += ` (${p.value}%)`
    }
    label += ` - ${getBrandLabel(p.brand_model_id)} ${useApiStore().getObjLabel(p.brand_model_id, 'brand_models')}`
    label += ` ${p.targa ?? p.telaio}`
  } else if (p.value && me.value.admin_can_see_amounts) label += ` | ${p.value} €/h`
  return label
}

export function hasTime(presence: Pick<Presence | Permit, 'start' | 'end'>): boolean {
  const start = dayjs(presence.start)
  const end = dayjs(presence.end)
  return start.hour() !== 0 || start.minute() !== 0 || end.hour() !== 23 || end.minute() !== 59
  // non necessario in quanto si crea un end sicuramente non 23.59
  // return (start.hour() !== 0 || start.minute() !== 0) && (end.hour() !== 23 || end.minute() !== 59)
}

export function toTime(val: number, wrap = true, xs = Screen.xs) {
  if (val === 0) return ''

  const hours = Math.floor(val)
  const min = Math.round((val % 1) * 60)
  const seconds = Math.round(((val % 1) * 60 % 1) * 60)
  const hoursStr = xs ? 'h' : (hours === 1 ? ' ora' : ' ore')
  const minStr = xs ? 'm' : ' min'
  const secStr = xs ? 's' : (seconds === 1 ? ' secondo' : ' secondi')

  let res = ''

  if (hours === 0) {
    if (min > 0) res += `${min}${minStr}`
    if (seconds > 0) res += ` ${seconds}${secStr}`
  } else {
    res += `${hours}${hoursStr}`
    if (min > 0) res += ` ${min}${minStr}`
  }

  return wrap ? `(${res})` : res
}

export const notAnInput = (cb: () => any) => (e: KeyboardEvent) => {
  if (
    e.target instanceof Element
    && e.target?.tagName !== 'INPUT'
    && e.target?.tagName !== 'TEXTAREA'
  ) {
    e.preventDefault()
    cb()
  }
}

type PickViewRoutes<S> = S extends `/${infer R}/[id]` ? R : never

export const entityBtnProps = (id: number | string, route: PickViewRoutes<keyof RouteNamedMap>, label?: string): WithClass<QBtnProps> => ({
  label: label ? noCaps(label) : undefined,
  class: 'text-weight-regular',
  flat: true,
  padding: 'none xs',
  size: '12px',
  dense: true,
  noCaps: true,
  to: { name: `/${route}/[id]`, params: { id } },
})

// export async function copyToClip(val: string, kind: string) {
//   try {
//     await copyToClipboard(val)
//     Notify.create({ type: 'positive', message: `${kind} copiato negli appunti`, icon: 'mdi-content-copy' })
//   } catch {
//     Notify.create({ type: 'positive', message: 'Il tuo browser non consente di copiare negli appunti' })
//   }
// }

export const searchFn = (filter: string, field: string) => field
  .toLowerCase()
  .includes(filter.toLowerCase())

export function omit<T extends Record<string, any>, K extends keyof T>(obj: T, key: K) {
  const { [key]: _, ...rest } = obj
  return rest
  // return Object.fromEntries<T[K]>(
  //   Object.entries(obj).filter(([k]) => k !== key),
  // )
}

// export const un = <T>(obj: string) => JSON.parse(obj) as T
// export function un<T>(obj: string) {
//   return computed(() => JSON.parse(obj) as T)
// }

export function conf(message: string, html = false, cancelLabel = 'Annulla') {
  return new Promise<boolean>(resolve => {
    Dialog.create({
      title: 'Attenzione',
      message,
      html,
      cancel: { color: 'gray', flat: true, label: cancelLabel },
      position: 'bottom',
      ok: 'Procedi',
      focus: 'cancel',
    })
      .onOk(() => resolve(true))
      .onCancel(() => resolve(false))
      .onDismiss(() => resolve(false))
  })
}

export const isWeekEnd = (date: dayjs.Dayjs) => date.format('d') === '0' || date.format('d') === '6'

export function flat(rows: Record<string, Record<string, Record<string, Presence[]>>>, n = 3) {
  return Object.values(rows).map(r => Object.values(r).map(r2 => Object.values(r2))).flat(n) as Presence[]
}
